import { ModalShell, showToast } from '@lokalise/louis'
import {
  listAiConfigurationsKeys,
  listTeamLanguagesKeys,
  useAssignAiConfigurationToProjectsMutation,
  useListTeamLanguagesQuery,
} from '@lokalise/shared'
import type { AiConfigLanguage, AiConfigProject } from '@lokalise/translation-api-schemas'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useCreateProfile } from '../../hooks/useCreateProfile'
import { useFetchProjects } from '../../hooks/useFetchProjects'
import { useFetchTags } from '../../hooks/useFetchTags'
import { useUpdateProfile } from '../../hooks/useUpdateProfile'
import {
  type Conflict,
  isConflictErrorResponse,
  isValidationErrorResponse,
  isWretchError,
} from '../../types/api'
import type { CreationConfigAllowedTypes, UserCreatedProfile } from '../../types/tag'
import {
  appHttpClient,
  authenticatedPublicApiHeaderBuilder,
  getTeamIdFromSession,
} from '../../utils/api'
import { formatValidationError } from '../../utils/error'
import { showUnexpectedErrorToast } from '../../utils/toast'
import { OverrideStep } from '../OverrideStep/OverrideStep'
import { StepController } from '../StepController/StepController'
import { ActivationStep } from './components/ActivationStep/ActivationStep'
import { DataSourceStep } from './components/DataSourceStep/DataSourceStep'
import { OverviewStep } from './components/OverviewStep/OverviewStep'
import { ProfileBasicsStep } from './components/ProfileBasicsStep/ProfileBasicsStep'
import { UpsellEvaluationStep } from './components/UpsellEvaluationStep/UpsellEvaluationStep'
import styles from './UserCreatedProfileModal.module.css'

type UserCreatedProfileModalProps = {
  profile?: UserCreatedProfile
  onClose: () => void
  enableEvaluations?: boolean
}

const OVERRIDE_STEP_INDEX = 4
const EVALUATION_UPSELL_STEP_INDEX = 5

export const UserCreatedProfileModal = ({
  profile,
  onClose,
  enableEvaluations,
}: UserCreatedProfileModalProps) => {
  const [id, setId] = useState(profile?.id ?? '')
  const [name, setName] = useState(profile?.name ?? '')
  const [sourceLang, setSourceLang] = useState<AiConfigLanguage | undefined>(
    profile?.config?.rag?.source_languages?.[0],
  )
  const [selectedTargetLangs, setSelectedTargetLangs] = useState<AiConfigLanguage[]>(
    profile?.config?.rag.target_languages ?? [],
  )
  const [selectedTag, setSelectedTag] = useState<string | null>(
    profile?.config?.rag?.tags?.[0] ?? null,
  )
  const [dataSource, setDataSource] = useState<CreationConfigAllowedTypes | null>(
    profile?.config?.rag.type ?? null,
  )
  const [sourceProjects, setSourceProjects] = useState<string[]>(
    profile?.config?.rag.project_uuids ?? [],
  ) // For reviewed status - which projects should examples be taken from?
  const [projectToAssign, setProjectsToAssign] = useState<AiConfigProject[]>(
    profile?.projects ?? [],
  ) // Which projects should this profile be assigned to?
  const [aiModelIntegration, setAiModelIntegration] = useState(profile?.config?.integration)

  const isEdit = !!profile

  const [conflicts, setConflicts] = useState<Conflict[]>([])
  const [stepIndex, setStepIndex] = useState(isEdit ? 1 : 0)

  const queryClient = useQueryClient()

  const teamId = getTeamIdFromSession()

  const profilesQueryKey = listAiConfigurationsKeys.teamAiConfigurationsKey(teamId)
  const sourceLangsQueryKey = listTeamLanguagesKeys.teamLanguagesKey({ teamId })
  const targetLangsQueryKey = listTeamLanguagesKeys.teamLanguagesKey({
    teamId,
    base_language_id: sourceLang?.id,
  })
  // Pre-fetch all data used within the modal
  // User must complete all steps, so they will need the data - prefetching it will make the modal feel faster
  const { data: sourceLangs, isPending: isLoadingSourceLangs } = useListTeamLanguagesQuery(
    appHttpClient,
    authenticatedPublicApiHeaderBuilder,
    { queryKey: sourceLangsQueryKey },
  )
  const { data: targetLangs, isPending: isLoadingTargetLangs } = useListTeamLanguagesQuery(
    appHttpClient,
    authenticatedPublicApiHeaderBuilder,
    { queryKey: targetLangsQueryKey },
  )
  const { data: tags, isPending: isLoadingTags } = useFetchTags()
  const { data: projects, isPending: isLoadingProjects } = useFetchProjects()

  const projectsToAssignIds = projectToAssign.map(({ id }) => id)
  const projectsToAssignWithBaseLangId =
    projects?.filter((option) => projectsToAssignIds.includes(option.id)) ?? []

  const onSuccess = () => {
    const title = isEdit
      ? `${name} has been updated successfully`
      : `${name} has been created and activated successfully.`
    showToast({ type: 'success', title })
    queryClient.invalidateQueries({ queryKey: profilesQueryKey })

    // Go to UpsellEvaluationStep if user just created a new profile
    if (!isEdit && enableEvaluations) {
      setStepIndex(EVALUATION_UPSELL_STEP_INDEX)
      return
    }

    onClose()
  }

  const onError = (error: unknown) => {
    if (isWretchError(error) && isConflictErrorResponse(error.json)) {
      setConflicts(error.json.details.conflicts)
      setStepIndex(OVERRIDE_STEP_INDEX)
      return
    }

    if (isWretchError(error) && isValidationErrorResponse(error.json)) {
      const message = formatValidationError(error.json.error)
      showToast({
        type: 'error',
        title: 'Something went wrong',
        description: <span className={styles.toast}>{message}</span>,
      })

      return
    }

    showUnexpectedErrorToast()
  }

  const { mutate: assignAiConfig, isPending: isAssigningAiConfig } =
    useAssignAiConfigurationToProjectsMutation(appHttpClient, authenticatedPublicApiHeaderBuilder, {
      onSuccess,
      onError,
    })

  const { create, isPending: isCreatingProfile } = useCreateProfile({
    onSuccess,
    onError,
    onCreateSuccess: ({ id }) => setId(id), // Will need the id for the override step if there's an assignment conflict
  })

  const { update, isPending: isUpdatingProfile } = useUpdateProfile({ onSuccess, onError })

  const onActivate = () => {
    // This should be an impossible state
    if (!dataSource) {
      throw new Error('TRAP: Attempting to create AI profile with no specified data source')
    }

    if (isEdit) {
      update({
        projects: projectToAssign.map((project) => project.id),
        profileId: profile.id,
        profile: {
          name,
          is_default: profile?.is_default,
          config: {
            rag: {
              source_languages: sourceLang ? [sourceLang.id] : undefined,
              target_languages: selectedTargetLangs.map((lang) => lang.id),
              type: dataSource,
              tags: selectedTag ? [selectedTag] : undefined,
              project_uuids: sourceProjects,
            },
            integration: aiModelIntegration,
          },
        },
      })
      return
    }

    create({
      projects: projectToAssign.map((project) => project.id),
      profile: {
        name,
        is_default: false,
        config: {
          rag: {
            type: dataSource,
            tags: selectedTag ? [selectedTag] : undefined,
            source_languages: sourceLang ? [sourceLang?.id] : undefined,
            target_languages: selectedTargetLangs.map((lang) => lang.id),
            project_uuids: sourceProjects,
          },
          integration: aiModelIntegration,
        },
      },
    })
  }

  const onForceAssign = async () =>
    assignAiConfig({
      project_ids: projectToAssign.map((project) => project.id),
      teamId,
      configurationId: id,
      force: true,
    })

  const onNext = () => setStepIndex((i) => i + 1)

  return (
    <ModalShell
      title={isEdit ? 'Update profile' : 'Create profile'}
      className={styles.modal}
      opened
      onClose={onClose}
    >
      <StepController stepIndex={stepIndex}>
        <OverviewStep onClose={onClose} onNext={onNext} />
        <ProfileBasicsStep
          onNext={onNext}
          onClose={onClose}
          name={name}
          isEdit={isEdit}
          onNameChange={setName}
          selectedAiModel={aiModelIntegration}
          onAiModelSelected={setAiModelIntegration}
        />
        <DataSourceStep
          sourceLangs={sourceLangs?.data?.base_languages}
          targetLangs={targetLangs?.data?.target_languages ?? []}
          selectedSourceLang={sourceLang}
          selectedTargetLangs={selectedTargetLangs}
          dataSource={dataSource}
          projects={projects ?? []}
          selectedProjects={sourceProjects}
          tags={tags?.data ?? []}
          selectedTag={selectedTag}
          integration={aiModelIntegration}
          isEdit={isEdit}
          isLoadingLanguages={isLoadingTargetLangs}
          isLoadingProjects={isLoadingProjects}
          isLoadingTags={isLoadingTags}
          isLoadingSourceLangs={isLoadingSourceLangs}
          name={name}
          onSourceLangChange={setSourceLang}
          onSelectedTargetLangsChange={setSelectedTargetLangs}
          onDataSourceChange={setDataSource}
          onProjectsChange={setSourceProjects}
          onTagChange={setSelectedTag}
          onClose={onClose}
          onNext={onNext}
        />
        <ActivationStep
          isEdit={isEdit}
          isActivating={isUpdatingProfile || isCreatingProfile}
          projects={projects ?? []}
          selectedProjects={projectsToAssignWithBaseLangId}
          name={name}
          targetLangs={selectedTargetLangs}
          sourceLang={sourceLang}
          integration={aiModelIntegration}
          onProjectsChange={setProjectsToAssign}
          onActivate={onActivate}
          onClose={onClose}
        />
        <OverrideStep
          onClose={onClose}
          onUpdate={onForceAssign}
          profileName={name}
          conflicts={conflicts}
          isOverriding={isAssigningAiConfig}
        />
        <UpsellEvaluationStep onClose={onClose} profileId={id} />
      </StepController>
    </ModalShell>
  )
}
